import MissionsRecurrentes, { ROLES_CODES_MISSION } from "./MissionsRecurrentes";
import React, { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { useEtatGlobal } from "../EtatGlobal.js";
import { authFetch, httpClient } from "../../HttpClient";
import { dialog } from "../../components/ui/Dialog";
import { useClients } from "./useClients.js";
import { normalizeText } from "../../utils/text.js";
import { PLATEFORME_ELECTRONIQUE_OPTIONS } from "./plateformeElectroniqueOptions";
import FormeSocialeSelect from "./kyc-lab/atoms/FormeSocialeSelect";
import {
  User,
  Building2,
  Calendar,
  Briefcase,
  Check,
  Loader2,
  UserPlus,
  Settings,
  MessageSquare,
  Mail,
  Search,
  Plus,
  X,
  Send,
  AlertTriangle,
} from "lucide-react";

// Clés de rapprochement pour la détection de doublon à la création.
// Nom : on ne garde que lettres et chiffres, donc casse, accents, espaces et
// ponctuation sont ignorés — "MAM'ZELLE PIZZA", "Mamzelle Pizza" et
// "mam zelle pizza" désignent le même dossier.
// SIREN : on ne garde que les chiffres. Un doublon réel est passé entre les
// mailles parce qu'un des deux SIREN portait une espace finale (incident du
// 02/10/2026) ; comparer les chiffres seuls supprime ce angle mort.
const cleNom = (v) => normalizeText(v).replace(/[^a-z0-9]/g, "");
const cleSiren = (v) => (v || "").replace(/\D/g, "");

function Create({
  onCancel,
  civilite,
  nom,
  nomClient,
  prenomClient,
  mail,
  telephone,
}) {
  const { utilisateur, users: allUsers, kycReferentiel } = useEtatGlobal();
  // Liste des dossiers existants, déjà en cache (React Query, chargée à la
  // connexion) : sert à signaler un doublon éventuel, sans requête réseau.
  const { data: clientsExistants = [] } = useClients();
  const queryClient = useQueryClient();
  const [newClient, setNewClient] = useState({
    civilite: civilite || "",
    nom: nom || "",
    nomClient: nomClient || "",
    prenomClient: prenomClient || "",
    email: mail || "",
    mdp: "",
    telephone: telephone || "",
    fonctionContact: "",
    civilite2: "",
    nomClient2: "",
    prenomClient2: "",
    fonctionContact2: "",
    email2: "",
    telephone2: "",
    formeSociale: "",
    siren: "",
    dateClotureJour: "01",
    dateClotureMois: "01",
    dateClotureAnnee: "2025",
    dateDebutMission: "",
    regimeFiscal: "",
    regimeTVA: "",
    taxeSalaires: "",
    expertComptable: "",
    superviseur: "",
    collaborateur: "",
    collaborateur_niv2: "",
    missionComptable: "non",
    missionSociale: "non",
    missionJuridique: "non",

    ponderation: "",
    activite: "",
    holding: false,
    personnePhysique: false,
    commentaire: "",
    groupement: "",
    cabinetId: utilisateur?.userCabinets?.[0]?.cabinetId ?? "",

    plateformeElectronique: "",
    accesEC: "non",

    // Courrier de reprise (en cours de développement)
    reprisAncienCabinet: false,
    ancienCabinetId: "",
    ancienCabinetNom: "",
  });
  const experts = allUsers.filter((u) => u.role === "EXPERT_COMPTABLE");
  // Courrier de reprise : pour l'instant, seul Samuel peut être signataire —
  // pas de choix possible tant que ce n'est pas ouvert aux autres EC.
  const signataireCourrierReprise =
    experts.find((e) => e.email === "samuel.hoarau@alfacomptabilite.com") || null;
  const collaborateurs = allUsers.filter((u) =>
    ["COLLABORATEUR", "SUPERVISEUR", "EXPERT_COMPTABLE"].includes(u.role)
  );
  // Niv. 2 accepte aussi les assistants (role ASSISTANT), contrairement à
  // Niv. 1 — décision confirmée suite à un ticket (Mélissa LANGLADE,
  // assistante, invisible dans ce menu alors qu'elle doit pouvoir y être
  // choisie).
  const collaborateurs_niv2 = allUsers.filter((u) =>
    ["COLLABORATEUR", "ASSISTANT"].includes(u.role)
  );
  const socials = allUsers.filter((u) => u.role === "SOCIAL");
  const juridiques = allUsers.filter((u) => u.role === "JURIDIQUE");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [groupements, setGroupements] = useState([]);
  const [showGroupementPanel, setShowGroupementPanel] = useState(false);
  const [groupementQuery, setGroupementQuery] = useState("");

  useEffect(() => {
    // Récupérer la liste des groupements depuis l'API
    httpClient
      .get("https://backend-myalfa.vercel.app/api/groupementsClients")
      .then((res) => {
        setGroupements(res.data); // res.data est un tableau de chaînes
      })
      .catch((err) => {
        console.error("Erreur lors du chargement des groupements :", err);
      });
  }, []);

  // Courrier de reprise (en cours de développement) — la case ouvre un
  // overlay par-dessus tout le formulaire pour saisir directement les
  // coordonnées de l'ancien cabinet. Cet enregistrement ne doit jamais
  // bloquer ni faire échouer la création du client.
  const [showAncienCabinetOverlay, setShowAncienCabinetOverlay] = useState(false);
  const [ancienCabinetForm, setAncienCabinetForm] = useState({
    nom: "",
    adresse: "",
    email: "",
    telephone: "",
  });
  const [isSavingAncienCabinet, setIsSavingAncienCabinet] = useState(false);

  // Cabinet ALFA (le nôtre) — nécessaire pour pré-remplir les variables
  // {denomination_cab}/{siege_cab}/{ville_rcs_cab}/{code_postal_cab}/
  // {lieu_demande} du courrier de reprise. Ne doit jamais bloquer la
  // création du client si l'appel échoue.
  const [cabinets, setCabinets] = useState([]);
  useEffect(() => {
    httpClient
      .get("https://backend-myalfa.vercel.app/api/cabinets")
      .then((res) => setCabinets(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Erreur lors du chargement des cabinets :", err));
  }, []);

  const selectedCabinet = useMemo(
    () => cabinets.find((c) => String(c.id) === String(newClient.cabinetId)) || null,
    [cabinets, newClient.cabinetId]
  );

  // Informations du courrier de reprise — pré-remplies depuis le formulaire
  // de création / le cabinet, mais toujours modifiables ici (une correction
  // ici n'affecte pas les champs du formulaire principal). Mapping vers les
  // variables du modèle Word ("public/courrier de reprise.docx") :
  // denominationCab→{denomination_cab}, siegeCab→{siege_cab},
  // villeRcsCab→{ville_rcs_cab}, codePostalCab→{code_postal_cab},
  // lieuDemande→{lieu_demande}, dateDemande→{date_demande},
  // nomDossier→{nom_client}, civiliteClient→{civilite_client},
  // nomContact→{nomClient}, prenomContact→{prenomClient},
  // formeSociale→{formeSociale}, dateDebutMission→{date_debut_mission}.
  // signataireId/signataireNom : expert-comptable dont la signature statique
  // sera utilisée dans le courrier.
  const [courrierForm, setCourrierForm] = useState({
    denominationCab: "",
    siegeCab: "",
    villeRcsCab: "",
    codePostalCab: "",
    lieuDemande: "",
    dateDemande: "",
    nomDossier: "",
    civiliteClient: "",
    nomContact: "",
    prenomContact: "",
    formeSociale: "",
    dateDebutMission: "",
    signataireId: "",
    signataireNom: "",
  });

  const getDefaultCourrierForm = () => ({
    denominationCab: newClient.courrierDenominationCab ?? selectedCabinet?.name ?? "",
    siegeCab: newClient.courrierSiegeCab ?? selectedCabinet?.address ?? "",
    villeRcsCab: newClient.courrierVilleRcsCab ?? selectedCabinet?.city ?? "",
    codePostalCab: newClient.courrierCodePostalCab ?? selectedCabinet?.postalCode ?? "",
    lieuDemande: newClient.courrierLieuDemande ?? selectedCabinet?.city ?? "",
    dateDemande: newClient.courrierDateDemande || new Date().toISOString().split("T")[0],
    nomDossier: newClient.courrierNomDossier ?? newClient.nom ?? "",
    civiliteClient: newClient.courrierCiviliteClient ?? newClient.civilite ?? "",
    nomContact: newClient.courrierNomContact ?? newClient.nomClient ?? "",
    prenomContact: newClient.courrierPrenomContact ?? newClient.prenomClient ?? "",
    formeSociale: newClient.courrierFormeSociale ?? newClient.formeSociale ?? "",
    dateDebutMission: newClient.courrierDateDebutMission ?? newClient.dateDebutMission ?? "",
    // Forcé à Samuel pour l'instant (cf. signataireCourrierReprise) — aucun
    // autre choix n'est proposé dans le formulaire.
    signataireId: signataireCourrierReprise ? String(signataireCourrierReprise.id) : "",
    signataireNom: signataireCourrierReprise
      ? `${signataireCourrierReprise.name} ${signataireCourrierReprise.lastName}`
      : "",
  });

  const handleOpenAncienCabinetOverlay = () => {
    setAncienCabinetForm({
      nom: newClient.ancienCabinetNom || "",
      adresse: newClient.ancienCabinetAdresse || "",
      email: newClient.ancienCabinetEmail || "",
      telephone: newClient.ancienCabinetTelephone || "",
    });
    setCourrierForm(getDefaultCourrierForm());
    setShowAncienCabinetOverlay(true);
  };

  const handleCancelAncienCabinetOverlay = () => {
    setShowAncienCabinetOverlay(false);
    if (!newClient.ancienCabinetId) {
      // Rien n'a encore été enregistré : on décoche la case.
      setNewClient((prev) => ({ ...prev, reprisAncienCabinet: false }));
    }
  };

  const handleRemoveAncienCabinet = () => {
    setNewClient((prev) => ({
      ...prev,
      reprisAncienCabinet: false,
      ancienCabinetId: "",
      ancienCabinetNom: "",
      ancienCabinetAdresse: "",
      ancienCabinetEmail: "",
      ancienCabinetTelephone: "",
      courrierDenominationCab: "",
      courrierSiegeCab: "",
      courrierVilleRcsCab: "",
      courrierCodePostalCab: "",
      courrierLieuDemande: "",
      courrierDateDemande: "",
      courrierNomDossier: "",
      courrierCiviliteClient: "",
      courrierNomContact: "",
      courrierPrenomContact: "",
      courrierFormeSociale: "",
      courrierDateDebutMission: "",
      courrierSignataireId: "",
      courrierSignataireNom: "",
    }));
  };

  const handleSaveAncienCabinet = () => {
    if (!ancienCabinetForm.nom.trim()) {
      dialog.alert("Le nom de l'ancien cabinet est requis.");
      return;
    }
    if (!ancienCabinetForm.email.trim()) {
      dialog.alert(
        "L'email de l'ancien cabinet est requis : c'est l'adresse à laquelle le courrier (PDF) sera envoyé."
      );
      return;
    }
    setIsSavingAncienCabinet(true);
    httpClient
      .post(
        "https://backend-myalfa.vercel.app/api/anciens-cabinets",
        ancienCabinetForm
      )
      .then((res) => {
        const created = res.data;
        setNewClient((prev) => ({
          ...prev,
          reprisAncienCabinet: true,
          ancienCabinetId: String(created.id),
          ancienCabinetNom: created.nom,
          ancienCabinetAdresse: created.adresse || "",
          ancienCabinetEmail: created.email || "",
          ancienCabinetTelephone: created.telephone || "",
          courrierDenominationCab: courrierForm.denominationCab,
          courrierSiegeCab: courrierForm.siegeCab,
          courrierVilleRcsCab: courrierForm.villeRcsCab,
          courrierCodePostalCab: courrierForm.codePostalCab,
          courrierLieuDemande: courrierForm.lieuDemande,
          courrierDateDemande: courrierForm.dateDemande,
          courrierNomDossier: courrierForm.nomDossier,
          courrierCiviliteClient: courrierForm.civiliteClient,
          courrierNomContact: courrierForm.nomContact,
          courrierPrenomContact: courrierForm.prenomContact,
          courrierFormeSociale: courrierForm.formeSociale,
          courrierDateDebutMission: courrierForm.dateDebutMission,
          courrierSignataireId: courrierForm.signataireId,
          courrierSignataireNom: courrierForm.signataireNom,
        }));
        setShowAncienCabinetOverlay(false);
      })
      .catch((err) => {
        console.error("Erreur lors de l'enregistrement de l'ancien cabinet :", err);
        dialog.alert("Erreur lors de l'enregistrement de l'ancien cabinet.");
      })
      .finally(() => setIsSavingAncienCabinet(false));
  };

  // Test d'envoi du courrier de reprise — génère le PDF depuis le modèle
  // Word (public/"courrier de reprise.docx", même mécanisme que
  // SignModif.js : PizZip + Docxtemplater → /api/upload → ConvertAPI), puis
  // l'envoie par mail à l'ancien cabinet avec demande d'accusé de
  // réception/lecture. STRICTEMENT indépendant de handleSubmit ci-dessous :
  // la création réelle d'un client ne déclenche jamais cet envoi.
  const [isTestingEnvoiCourrier, setIsTestingEnvoiCourrier] = useState(false);

  const formatDateFr = (isoDate) => {
    if (!isoDate) return "";
    const [y, m, d] = isoDate.split("-");
    return d && m && y ? `${d}/${m}/${y}` : isoDate;
  };

  const handleTestEnvoiCourrier = async () => {
    if (!ancienCabinetForm.email.trim()) {
      dialog.alert("Renseignez l'email de l'ancien cabinet avant de tester l'envoi.");
      return;
    }
    setIsTestingEnvoiCourrier(true);
    try {
      const response = await fetch("/courrier de reprise.docx");
      if (!response.ok) {
        throw new Error("Modèle Word introuvable (public/courrier de reprise.docx).");
      }
      const arrayBuffer = await response.arrayBuffer();
      const zip = new PizZip(arrayBuffer);
      const doc = new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true });

      doc.render({
        denomination_cab: courrierForm.denominationCab || "",
        siege_cab: courrierForm.siegeCab || "",
        ville_rcs_cab: courrierForm.villeRcsCab || "",
        code_postal_cab: courrierForm.codePostalCab || "",
        lieu_demande: courrierForm.lieuDemande || "",
        date_demande: formatDateFr(courrierForm.dateDemande),
        nom_client: courrierForm.nomDossier || "",
        civilite_client: courrierForm.civiliteClient || "",
        nomClient: courrierForm.nomContact || "",
        prenomClient: courrierForm.prenomContact || "",
        formeSociale: courrierForm.formeSociale || "",
        date_debut_mission: formatDateFr(courrierForm.dateDebutMission),
      });

      const output = doc.getZip().generate({ type: "blob" });

      const formDataToSend = new FormData();
      formDataToSend.append("file", output, "courrier-de-reprise-test.docx");

      const uploadResponse = await authFetch(
        "https://backend-myalfa.vercel.app/api/upload",
        { method: "POST", body: formDataToSend }
      );
      if (!uploadResponse.ok) {
        throw new Error("Erreur lors de la conversion en PDF.");
      }
      const { pdfUrl } = await uploadResponse.json();

      const sendResponse = await authFetch(
        "https://backend-myalfa.vercel.app/api/courrier-reprise/test-envoi",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: ancienCabinetForm.email,
            pdfUrl,
            nomAncienCabinet: ancienCabinetForm.nom,
            nomDossier: courrierForm.nomDossier,
            denominationCab: courrierForm.denominationCab,
            createurId: utilisateur?.id,
          }),
        }
      );
      if (!sendResponse.ok) {
        const errText = await sendResponse.text();
        throw new Error(errText || "Erreur lors de l'envoi du mail de test.");
      }

      dialog.alert(`Mail de test envoyé à ${ancienCabinetForm.email}.`);
    } catch (err) {
      console.error("Erreur lors du test d'envoi du courrier de reprise :", err);
      dialog.alert("Erreur lors du test d'envoi : " + err.message);
    } finally {
      setIsTestingEnvoiCourrier(false);
    }
  };

  // Envoi RÉEL du courrier de reprise à la création du client — même
  // mécanisme que handleTestEnvoiCourrier, mais lu depuis les champs
  // persistés sur newClient (ancienCabinetEmail, courrier*) plutôt que
  // depuis l'état local de l'overlay, et vers /courrier-reprise/envoi (pas
  // test-envoi). Appelé uniquement si la case est cochée ET qu'un ancien
  // cabinet a bien été enregistré. Volontairement best-effort : ne doit
  // jamais faire échouer ni retarder la création du client elle-même — voir
  // l'appel dans handleSubmit, qui ne l'attend pas (fire-and-forget).
  const envoyerCourrierRepriseAutomatique = async () => {
    if (!newClient.reprisAncienCabinet || !newClient.ancienCabinetEmail) return;

    try {
      const response = await fetch("/courrier de reprise.docx");
      if (!response.ok) {
        throw new Error("Modèle Word introuvable (public/courrier de reprise.docx).");
      }
      const arrayBuffer = await response.arrayBuffer();
      const zip = new PizZip(arrayBuffer);
      const doc = new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true });

      doc.render({
        denomination_cab: newClient.courrierDenominationCab || "",
        siege_cab: newClient.courrierSiegeCab || "",
        ville_rcs_cab: newClient.courrierVilleRcsCab || "",
        code_postal_cab: newClient.courrierCodePostalCab || "",
        lieu_demande: newClient.courrierLieuDemande || "",
        date_demande: formatDateFr(newClient.courrierDateDemande),
        nom_client: newClient.courrierNomDossier || "",
        civilite_client: newClient.courrierCiviliteClient || "",
        nomClient: newClient.courrierNomContact || "",
        prenomClient: newClient.courrierPrenomContact || "",
        formeSociale: newClient.courrierFormeSociale || "",
        date_debut_mission: formatDateFr(newClient.courrierDateDebutMission),
      });

      const output = doc.getZip().generate({ type: "blob" });

      const formDataToSend = new FormData();
      formDataToSend.append("file", output, "courrier-de-reprise.docx");

      const uploadResponse = await authFetch(
        "https://backend-myalfa.vercel.app/api/upload",
        { method: "POST", body: formDataToSend }
      );
      if (!uploadResponse.ok) {
        throw new Error("Erreur lors de la conversion en PDF.");
      }
      const { pdfUrl } = await uploadResponse.json();

      const sendResponse = await authFetch(
        "https://backend-myalfa.vercel.app/api/courrier-reprise/envoi",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: newClient.ancienCabinetEmail,
            pdfUrl,
            nomAncienCabinet: newClient.ancienCabinetNom,
            nomDossier: newClient.courrierNomDossier,
            denominationCab: newClient.courrierDenominationCab,
            createurId: utilisateur?.id,
          }),
        }
      );
      if (!sendResponse.ok) {
        const errText = await sendResponse.text();
        throw new Error(errText || "Erreur lors de l'envoi du courrier de reprise.");
      }
    } catch (err) {
      console.error("Erreur lors de l'envoi automatique du courrier de reprise :", err);
    }
  };

  const handleSelectGroupement = (value) => {
    setNewClient((prev) => ({ ...prev, groupement: value }));
    setShowGroupementPanel(false);
    setGroupementQuery("");
  };

  const handleClearGroupement = () => {
    setNewClient((prev) => ({ ...prev, groupement: "" }));
  };

  // Récupération des utilisateurs et filtrage pour les experts comptables

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewClient((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Le cabinet est systématiquement rattaché (accès identifiants) pour ces
  // trois PA — les autres nécessitent une action du client, donc pas de
  // rattachement présumé (demande du 03/09/2026, même règle qu'Update.js).
  const PA_ACCES_EC_PAR_DEFAUT = ["DEXT", "MEG", "MyAlfa Link"];

  const handlePlateformeElectroniqueChange = (e) => {
    const value = e.target.value;
    setNewClient((prev) => ({
      ...prev,
      plateformeElectronique: value,
      accesEC: PA_ACCES_EC_PAR_DEFAUT.includes(value) ? "oui" : "non",
    }));
  };

  const handleSelectExpert = (e) => {
    const selectedName = e.target.value;
    const selectedExpert = experts.find(
      (expert) => `${expert.name} ${expert.lastName}` === selectedName
    );

    setNewClient((prev) => ({
      ...prev,
      expertComptable: selectedName,
      email_expertComptable: selectedExpert?.email || "",
      id_expertComptable: selectedExpert?.id || "",
    }));

    if (selectedExpert) {
      console.log("Email de l'expert sélectionné :", selectedExpert.email);
      console.log("id de l'expert sélectionné :", selectedExpert.id);
    } else {
      console.warn("Aucun expert trouvé pour :", selectedName);
    }
  };

  const handleSelectCollaborateur = (e) => {
    const selectedName = e.target.value;
    const selectedCollaborateur = collaborateurs.find(
      (collab) => `${collab.name} ${collab.lastName}` === selectedName
    );

    setNewClient((prev) => ({
      ...prev,
      collaborateur: selectedName,
      email_collaborateur: selectedCollaborateur?.email || "",
      id_collaborateur: selectedCollaborateur?.id || "",
    }));

    if (selectedCollaborateur) {
      console.log(
        "Email du collaborateur sélectionné :",
        selectedCollaborateur.email
      );
      console.log(
        "id du collaborateur sélectionné :",
        selectedCollaborateur.id
      );
    } else {
      console.warn("Aucun collaborateur trouvé pour :", selectedName);
    }
  };

  const handleSelectCollaborateur_niv2 = (e) => {
    const selectedName = e.target.value;

    const selectedCollaborateur_niv2 = collaborateurs_niv2.find(
      (sup) => `${sup.name} ${sup.lastName}` === selectedName
    );

    setNewClient((prev) => ({
      ...prev,
      collaborateur_niv2: selectedName,
      email_collaborateur_niv2: selectedCollaborateur_niv2?.email || "",
      id_collaborateur_niv2: selectedCollaborateur_niv2?.id || "",
    }));

    if (selectedCollaborateur_niv2) {
      console.log(
        "Email du collaborateur_niv2 sélectionné :",
        selectedCollaborateur_niv2.email
      );
      console.log(
        "id du collaborateur_niv2 sélectionné :",
        selectedCollaborateur_niv2.id
      );
    } else {
      console.warn("Aucun collaborateur_niv2 trouvé pour :", selectedName);
    }
  };

  const handleSelectSocial = (e) => {
    const selectedName = e.target.value;

    const selectedSocial = socials.find(
      (sup) => `${sup.name} ${sup.lastName}` === selectedName
    );

    setNewClient((prev) => ({
      ...prev,
      social: selectedName,
      email_social: selectedSocial?.email || "",
      id_social: selectedSocial?.id || "",
    }));

    if (selectedSocial) {
      console.log("Email du social sélectionné :", selectedSocial.email);
      console.log("id du social sélectionné :", selectedSocial.id);
    } else {
      console.warn("Aucun social trouvé pour :", selectedName);
    }
  };

  const handleSelectJuridique = (e) => {
    const selectedName = e.target.value;

    const selectedJuridique = juridiques.find(
      (sup) => `${sup.name} ${sup.lastName}` === selectedName
    );

    setNewClient((prev) => ({
      ...prev,
      juridique: selectedName,
      email_juridique: selectedJuridique?.email || "",
      id_juridique: selectedJuridique?.id || "",
    }));

    if (selectedJuridique) {
      console.log("Email du Juridique sélectionné :", selectedJuridique.email);
      console.log("id du Juridique sélectionné :", selectedJuridique.id);
    } else {
      console.warn("Aucun Juridique trouvé pour :", selectedName);
    }
  };

  const handleDateClotureChange = (e) => {
    const { name, value } = e.target;
    if (name === "date_cloture_jour") {
      setNewClient((prev) => ({
        ...prev,
        dateClotureJour: value,
      }));
    } else if (name === "date_cloture_mois") {
      setNewClient((prev) => ({
        ...prev,
        dateClotureMois: value,
      }));
    }
  };

  const handleMissionToggle = (item) => {
    setNewClient((prev) => ({
      ...prev,
      [item]: prev[item] === "oui" ? "non" : "oui",
    }));
  };

  const handleRepriseAncienCabinetToggle = () => {
    if (newClient.reprisAncienCabinet) {
      handleRemoveAncienCabinet();
    } else {
      handleOpenAncienCabinetOverlay();
    }
  };

  const handleHoldingToggle = () => {
    setNewClient((prev) => {
      const newHolding = !prev.holding;
      return {
        ...prev,
        holding: newHolding,
      };
    });
  };

  // Dossiers existants portant déjà le nom saisi, puis le SIREN saisi. Filet
  // volontairement large : ce sont des AVERTISSEMENTS, jamais des blocages —
  // deux structures peuvent légitimement porter le même nom, et la création
  // reste possible après confirmation explicite (voir handleSubmit).
  const doublonsNom = useMemo(() => {
    const saisi = cleNom(newClient.nom);
    if (saisi.length < 3) return [];
    return clientsExistants.filter((c) => cleNom(c.nom) === saisi);
  }, [newClient.nom, clientsExistants]);

  // Un SIREN est à 9 chiffres : en deçà, la saisie est encore en cours.
  const doublonsSiren = useMemo(() => {
    const saisi = cleSiren(newClient.siren);
    if (saisi.length < 9) return [];
    return clientsExistants.filter((c) => cleSiren(c.siren) === saisi);
  }, [newClient.siren, clientsExistants]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Doublon potentiel : on demande confirmation au lieu de refuser.
    const motifs = [];
    if (doublonsNom.length > 0) {
      motifs.push(
        `le nom « ${newClient.nom.trim()} » (${doublonsNom.length} dossier${
          doublonsNom.length > 1 ? "s" : ""
        })`
      );
    }
    if (doublonsSiren.length > 0) {
      motifs.push(
        `le SIREN ${newClient.siren.trim()} — ${doublonsSiren
          .map((c) => c.nom)
          .join(", ")}`
      );
    }
    if (motifs.length > 0) {
      const confirme = await dialog.confirm(
        `Un dossier existe déjà avec ${motifs.join(
          " et "
        )}. Voulez-vous quand même créer ce dossier ?`,
        { variant: "default", confirmLabel: "Créer quand même" }
      );
      if (!confirme) return;
    }

    // if (!canEditClient(utilisateur, newClient.cabinetId || null)) {
    //   dialog.alert("Vous n'avez pas les droits pour créer un client dans ce cabinet.");
    //   return;
    // }

    const fullDateCloture = `${newClient.dateClotureAnnee}-${newClient.dateClotureMois}-${newClient.dateClotureJour}`;
    const clientToSend = {
      ...newClient,
      dateCloture: fullDateCloture,
      // Espaces de bord retirés : c'est une espace finale sur un SIREN qui a
      // laissé passer le doublon MAM'ZELLE PIZZA (02/10/2026).
      siren: newClient.siren.trim(),
    };

    setIsLoading(true);
    setError(null);

    authFetch("https://backend-myalfa.vercel.app/api/clients", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        Object.fromEntries(
          Object.entries(clientToSend).filter(
            ([key, value]) => !(key === "commentaire" && value.trim() === "")
          )
        )
      ),
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Erreur HTTP ${response.status} : ${errorText}`);
        }
        return response.json();
      })
      .then((data) => {
        // Mise à jour optimiste du cache clients partagé
        queryClient.setQueryData(["clients"], (prevClients) =>
          Array.isArray(prevClients)
            ? [...prevClients, { id: data.clientId, nom: newClient.nom }]
            : prevClients
        );

        // Courrier de reprise à l'ancien cabinet — fire-and-forget : ne doit
        // jamais bloquer ni faire échouer la création du client, donc pas de
        // `return`/`await` ici (une erreur dedans est déjà interceptée en
        // interne par envoyerCourrierRepriseAutomatique).
        envoyerCourrierRepriseAutomatique();

        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + 5);
        const dueDateString = dueDate.toISOString().split("T")[0];

        const userIds = [
          newClient.id_collaborateur_niv2,
          newClient.id_collaborateur,
          newClient.id_expertComptable,
          // newClient.id_social,
          // newClient.id_juridique,
        ].filter(Boolean);

        // Premier todo
        const todoPayload1 = {
          title: "DPA-0",
          status: "En cours",
          type: "Preparation",
          nomClient: newClient.nom,
          clientId: data.clientId,
          userIds: [39],
          nature: "Preparation / Organisation",
          dueDate: dueDateString,
          priority: "classique",
        };

        // Deuxième todo
        const todoPayload2 = {
          title: "Diligences NpMQ & LAB",
          status: "En cours",
          type: "Diligences",
          nomClient: newClient.nom,
          clientId: data.clientId,
          userIds,
          nature: "Preparation / Organisation",
          dueDate: dueDateString,
          priority: "classique",
        };

        // Troisième todo
        const todoPayload3 = {
          title: "Vérification des premiers obligations fiscales",
          status: "En cours",
          type: "Verification",
          nomClient: newClient.nom,
          clientId: data.clientId,
          userIds,
          nature: "Verification",
          dueDate: dueDateString,
          priority: "classique",
        };

        // Quatrième todo
        const todoPayload4 = {
          title: "Paramétrage comptabilité/dext",
          status: "En cours",
          type: "dext",
          nomClient: newClient.nom,
          clientId: data.clientId,
          userIds,
          nature: "dext",
          dueDate: dueDateString,
          priority: "classique",
        };

        // Cinquième todo (Mandat impôts.gouv.fr)
        const todoPayload5 = {
          title: "Mandat impôts.gouv.fr",
          status: "En cours",
          type: "Mandat_impots",
          nomClient: newClient.nom,
          clientId: data.clientId,
          userIds,
          nature: "Mandat",
          dueDate: dueDateString,
          priority: "classique",
        };

        // Sixième todo (Urssaf.fr)
        const todoPayload6 = {
          title: "Urssaf.fr",
          status: "En cours",
          type: "Urssaf",
          nomClient: newClient.nom,
          clientId: data.clientId,
          userIds,
          nature: "Urssaf",
          dueDate: dueDateString,
          priority: "classique",
        };

        // Septième todo (Mise en place EBICS)
        const todoPayload7 = {
          title: "Mise en place EBICS",
          status: "En cours",
          type: "EBICS",
          nomClient: newClient.nom,
          clientId: data.clientId,
          userIds,
          nature: "EBICS",
          dueDate: dueDateString,
          priority: "classique",
        };

        // Envoi des 7 todos en parallele
        return Promise.all(
          [todoPayload1, todoPayload2, todoPayload3, todoPayload4, todoPayload5, todoPayload6, todoPayload7].map(
            (payload) =>
              authFetch("https://backend-myalfa.vercel.app/api/todos", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
              }).then((response) => {
                if (!response.ok) throw new Error(`Erreur création todo: ${payload.title}`);
                return response.json();
              })
          )
        );
      })

      .then((todoData) => {
        console.log("Todo créée avec succès :", todoData);

        // Envoi du mail
        return authFetch(
          "https://backend-myalfa.vercel.app/api/mail-create-client",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              nom_dossier: newClient.nom,
              formeSociale: newClient.formeSociale,
              regimeFiscal: newClient.regimeFiscal,
              regimeTVA: newClient.regimeTVA,
              activite: newClient.activite,
              commentaire: newClient.commentaire,
              missionComptable: newClient.missionComptable,
              missionSociale: newClient.missionSociale,
              missionJuridique: newClient.missionJuridique,
              email_collaborateur: newClient.email_collaborateur || "",
              email_collaborateur_niv2:
                newClient.email_collaborateur_niv2 || "",
              email_social: newClient.email_social || "",
              email_juridique: newClient.email_juridique || "",
              groupement: newClient.groupement || "",
            }),
          }
        );
      })
      .then(async (response) => {
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Erreur envoi mail : ${errorText}`);
        }
        return response.json();
      })
      .then((mailRes) => {
        console.log("Mail envoyé avec succès :", mailRes);
        onCancel();
      })
      .catch((error) => {
        console.error("Erreur dans le processus :", error);
        setError(
          "Une erreur est survenue lors de la création du client ou des actions associées."
        );
      })
      .finally(() => {
        queryClient.invalidateQueries({ queryKey: ["clients"] });
        setIsLoading(false);
        dialog.alert(
          "Le client a été ajouté avec succès à la base de données !"
        );
      });
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 relative">
      {/* En-tête */}
      <div className="flex items-center justify-between px-6 pb-4 border-b border-slate-100 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#9B1B5D] flex items-center justify-center">
            <UserPlus className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 leading-tight">
              Ajouter un Client
            </h2>
            <p className="text-xs text-slate-400">
              Créer un nouveau dossier client
            </p>
          </div>
        </div>
      </div>

      {/* Corps du formulaire */}
      <form
        onSubmit={handleSubmit}
        className="flex-1 overflow-y-auto px-6 py-4 space-y-4"
      >
        {/* ── Identité Client ── */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <User className="w-4 h-4 text-[#9B1B5D]" />
            <span className="text-sm font-semibold text-slate-700">
              Identité Client
            </span>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">
                Structure / Dossier
              </label>
              <input
                type="text"
                name="nom"
                placeholder="Nom du dossier"
                value={newClient.nom}
                onChange={handleChange}
                required
                className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
              />
              {doublonsNom.length > 0 && (
                <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>
                    {doublonsNom.length === 1
                      ? "Un dossier porte déjà ce nom"
                      : `${doublonsNom.length} dossiers portent déjà ce nom`}{" "}
                    :{" "}
                    <strong className="font-semibold">
                      {doublonsNom.map((c) => c.nom).join(", ")}
                    </strong>
                    . Vérifiez qu'il ne s'agit pas d'un doublon — la création
                    reste possible.
                  </span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Civilité
                </label>
                <select
                  name="civilite"
                  value={
                    newClient.civilite === ""
                      ? ""
                      : ["M.", "Mme", "Madame"].includes(newClient.civilite)
                      ? newClient.civilite
                      : "M."
                  }
                  onChange={handleChange}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                >
                  <option value="">Civilité</option>
                  <option value="M.">Monsieur</option>
                  {["Mme", "Madame"].map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Nom
                </label>
                <input
                  type="text"
                  name="nomClient"
                  placeholder="Nom du Client"
                  value={newClient.nomClient}
                  onChange={handleChange}
                  required
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Prénom
                </label>
                <input
                  type="text"
                  name="prenomClient"
                  placeholder="Prénom du Client"
                  value={newClient.prenomClient}
                  onChange={handleChange}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Fonction
                </label>
                <input
                  type="text"
                  name="fonctionContact"
                  placeholder="Ex. Gérant, Assistante de gestion..."
                  value={newClient.fonctionContact}
                  onChange={handleChange}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Coordonnées ── */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <Mail className="w-4 h-4 text-[#9B1B5D]" />
            <span className="text-sm font-semibold text-slate-700">
              Coordonnées
            </span>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">
                Email
              </label>
              <input
                type="text"
                name="email"
                placeholder="Email du Client"
                value={newClient.email}
                onChange={handleChange}
                required
                className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">
                Mot de passe
              </label>
              <input
                type="text"
                name="mdp"
                placeholder="Mot de passe du Client"
                value={newClient.mdp}
                onChange={handleChange}
                required
                className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">
                Téléphone
              </label>
              <input
                type="text"
                name="telephone"
                placeholder="Téléphone du Client"
                value={newClient.telephone}
                onChange={handleChange}
                required
                className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
              />
            </div>
          </div>
        </div>

        {/* ── 2e contact représentant (optionnel) ── */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <UserPlus className="w-4 h-4 text-[#9B1B5D]" />
            <span className="text-sm font-semibold text-slate-700">
              2e contact représentant (optionnel)
            </span>
          </div>
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Civilité
                </label>
                <select
                  name="civilite2"
                  value={newClient.civilite2}
                  onChange={handleChange}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                >
                  <option value="">Civilité</option>
                  <option value="M.">Monsieur</option>
                  {["Mme", "Madame"].map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Nom
                </label>
                <input
                  type="text"
                  name="nomClient2"
                  placeholder="Nom du 2e contact"
                  value={newClient.nomClient2}
                  onChange={handleChange}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Prénom
                </label>
                <input
                  type="text"
                  name="prenomClient2"
                  placeholder="Prénom du 2e contact"
                  value={newClient.prenomClient2}
                  onChange={handleChange}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Fonction
                </label>
                <input
                  type="text"
                  name="fonctionContact2"
                  placeholder="Ex. Gérant, Assistante de gestion..."
                  value={newClient.fonctionContact2}
                  onChange={handleChange}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Email
                </label>
                <input
                  type="text"
                  name="email2"
                  placeholder="Email du 2e contact"
                  value={newClient.email2}
                  onChange={handleChange}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Téléphone
                </label>
                <input
                  type="text"
                  name="telephone2"
                  placeholder="Téléphone du 2e contact"
                  value={newClient.telephone2}
                  onChange={handleChange}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Dates clés ── */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <Calendar className="w-4 h-4 text-[#9B1B5D]" />
            <span className="text-sm font-semibold text-slate-700">
              Dates clés
            </span>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">
                Date de début de mission
                <span className="ml-1 font-normal text-slate-400 italic">
                  (le dossier apparaîtra dans SuiviProd à partir de cette année
                  — vide = toutes les années)
                </span>
              </label>
              <input
                type="date"
                name="dateDebutMission"
                value={newClient.dateDebutMission}
                onChange={handleChange}
                className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
              />
            </div>
            <label className="block text-xs font-semibold text-slate-500 -mb-2">
              Date de clôture
            </label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Jour
                </label>
                <select
                  name="date_cloture_jour"
                  value={newClient.dateClotureJour}
                  onChange={handleDateClotureChange}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                >
                  {[...Array(31).keys()].map((day) => (
                    <option key={day} value={String(day + 1).padStart(2, "0")}>
                      {String(day + 1).padStart(2, "0")}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Mois
                </label>
                <select
                  name="date_cloture_mois"
                  value={newClient.dateClotureMois}
                  onChange={handleDateClotureChange}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                >
                  {[
                    "Janvier",
                    "Février",
                    "Mars",
                    "Avril",
                    "Mai",
                    "Juin",
                    "Juillet",
                    "Août",
                    "Septembre",
                    "Octobre",
                    "Novembre",
                    "Décembre",
                  ].map((month, index) => (
                    <option
                      key={index}
                      value={String(index + 1).padStart(2, "0")}
                    >
                      {month}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Année
                </label>
                <input
                  type="number"
                  name="dateClotureAnnee"
                  value={newClient.dateClotureAnnee}
                  onChange={handleChange}
                  min="2000"
                  max="2100"
                  placeholder="Année"
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Détails Entreprise ── */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <Building2 className="w-4 h-4 text-[#9B1B5D]" />
            <span className="text-sm font-semibold text-slate-700">
              Détails Entreprise
            </span>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">
                SIREN <span className="font-normal text-slate-400">(optionnel)</span>
              </label>
              <input
                type="text"
                name="siren"
                placeholder="9 chiffres"
                value={newClient.siren}
                onChange={handleChange}
                className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
              />
              {doublonsSiren.length > 0 && (
                <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>
                    Ce SIREN est déjà enregistré sur{" "}
                    <strong className="font-semibold">
                      {doublonsSiren.map((c) => c.nom).join(", ")}
                    </strong>
                    . Vérifiez qu'il ne s'agit pas d'un doublon — la création
                    reste possible.
                  </span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Forme Sociale
                </label>
                <FormeSocialeSelect
                  formes={kycReferentiel?.listes?.formes}
                  value={newClient.formeSociale}
                  onChange={(v) => handleChange({ target: { name: "formeSociale", value: v } })}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Régime Fiscal
                </label>
                <select
                  name="regimeFiscal"
                  value={newClient.regimeFiscal}
                  onChange={handleChange}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                >
                  <option value="">Régime Fiscal</option>
                  {[
                    "IS RS",
                    "IS RN",
                    "IR BIC Micro",
                    "IR BIC RS",
                    "IR BIC RN",
                    "IR BNC micro",
                    "IR BNC réel",
                    "IR RF",
                    "IR LMNP",
                  ].map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Régime TVA
                </label>
                <select
                  name="regimeTVA"
                  value={newClient.regimeTVA}
                  onChange={handleChange}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                >
                  <option value="">Régime TVA</option>
                  {["N/A", "Franchise", "RSI", "RN", "Mini réel"].map(
                    (item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    )
                  )}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Taxe salariale
                </label>
                <select
                  name="taxeSalaires"
                  value={newClient.taxeSalaires}
                  onChange={handleChange}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                >
                  <option value="">Taxe salariale</option>
                  {["Annuel", "Trimestriel", "Mensuel"].map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Activité
                </label>
                <input
                  type="text"
                  name="activite"
                  value={newClient.activite}
                  onChange={handleChange}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Pondération
                </label>
                <input
                  type="text"
                  name="ponderation"
                  value={newClient.ponderation}
                  onChange={handleChange}
                  className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">
                Solution de gestion / PA
                <span className="ml-1 font-normal text-slate-400 italic">
                  (optionnel)
                </span>
              </label>
              <select
                name="plateformeElectronique"
                value={newClient.plateformeElectronique}
                onChange={handlePlateformeElectroniqueChange}
                className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
              >
                <option value="">-- Aucune --</option>
                {PLATEFORME_ELECTRONIQUE_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">
                Accès EC
                <span className="ml-1 font-normal text-slate-400 italic">
                  (optionnel)
                </span>
              </label>
              <select
                name="accesEC"
                value={newClient.accesEC}
                onChange={handleChange}
                className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
              >
                <option value="non">Non</option>
                <option value="oui">Oui</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">
                Groupement
              </label>
              {newClient.groupement ? (
                <div className="flex items-center justify-between gap-2 h-10 px-3 rounded-lg bg-white border border-gray-200">
                  <span className="text-sm text-slate-700 truncate">
                    {newClient.groupement}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowGroupementPanel(true)}
                      className="text-xs font-medium text-[#9B1B5D] hover:underline"
                    >
                      Changer
                    </button>
                    <button
                      type="button"
                      onClick={handleClearGroupement}
                      className="text-slate-300 hover:text-slate-500 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowGroupementPanel(true)}
                  className="flex items-center gap-2 h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 text-slate-400 outline-none hover:border-[#9B1B5D]/40 hover:text-slate-600 transition-all"
                >
                  <Search className="w-3.5 h-3.5 shrink-0" />
                  Rechercher ou créer un groupement
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Options ── */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <Settings className="w-4 h-4 text-[#9B1B5D]" />
            <span className="text-sm font-semibold text-slate-700">
              Options
            </span>
          </div>
          <div className="p-4 space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={newClient.holding === true}
                onChange={handleHoldingToggle}
                className="accent-[#9B1B5D] w-4 h-4"
              />
              <span className="text-sm text-slate-700">Holding</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={newClient.personnePhysique === true}
                onChange={() => setNewClient((prev) => ({ ...prev, personnePhysique: !prev.personnePhysique }))}
                className="accent-[#9B1B5D] w-4 h-4"
              />
              <span className="text-sm text-slate-700">
                L'associé unique est personne physique et dirigeant
              </span>
            </label>
          </div>
        </div>

        {/* ── Missions ── */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <Briefcase className="w-4 h-4 text-[#9B1B5D]" />
            <span className="text-sm font-semibold text-slate-700">
              Missions
            </span>
          </div>
          <div className="p-4 space-y-4">
            {/* Mission Comptable */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newClient.missionComptable === "oui"}
                  onChange={() => handleMissionToggle("missionComptable")}
                  className="accent-[#9B1B5D] w-4 h-4"
                />
                <span className="text-sm font-semibold text-slate-700">
                  Mission Comptable
                </span>
              </label>
              {newClient.missionComptable === "oui" && (
                <div className="grid grid-cols-3 gap-3 pl-6">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">
                      Expert Comptable
                    </label>
                    <select
                      name="expertComptable"
                      value={newClient.expertComptable}
                      onChange={handleSelectExpert}
                      className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                    >
                      <option value="">Expert Comptable</option>
                      {experts.map((expert) => (
                        <option
                          key={expert.id}
                          value={`${expert.name} ${expert.lastName}`}
                        >
                          {expert.name} {expert.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">
                      Collaborateur Niv. 1
                    </label>
                    <select
                      name="collaborateur"
                      value={newClient.collaborateur}
                      onChange={handleSelectCollaborateur}
                      className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                    >
                      <option value="">Niv 1</option>
                      {collaborateurs
                        .filter((collab) =>
                          [
                            "COLLABORATEUR",
                            "SUPERVISEUR",
                            "EXPERT_COMPTABLE",
                          ].includes(collab.role)
                        )
                        .map((collab) => (
                          <option
                            key={collab.id}
                            value={`${collab.name} ${collab.lastName}`}
                          >
                            {collab.name} {collab.lastName}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">
                      Collaborateur Niv. 2
                    </label>
                    <select
                      name="collaborateur_niv2"
                      value={newClient.collaborateur_niv2}
                      onChange={handleSelectCollaborateur_niv2}
                      className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                    >
                      <option value="">Niv 2</option>
                      {collaborateurs_niv2.map((sup) => (
                        <option
                          key={sup.id}
                          value={`${sup.name} ${sup.lastName}`}
                        >
                          {sup.name} {sup.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Mission Sociale */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newClient.missionSociale === "oui"}
                  onChange={() => handleMissionToggle("missionSociale")}
                  className="accent-[#9B1B5D] w-4 h-4"
                />
                <span className="text-sm font-semibold text-slate-700">
                  Mission Sociale
                </span>
              </label>
              {newClient.missionSociale === "oui" && (
                <div className="pl-6">
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Responsable Social
                  </label>
                  <select
                    name="social"
                    value={newClient.social}
                    onChange={handleSelectSocial}
                    className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                  >
                    <option value="">Social</option>
                    {socials.map((sup) => (
                      <option
                        key={sup.id}
                        value={`${sup.name} ${sup.lastName}`}
                      >
                        {sup.name} {sup.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Mission Juridique */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newClient.missionJuridique === "oui"}
                  onChange={() => handleMissionToggle("missionJuridique")}
                  className="accent-[#9B1B5D] w-4 h-4"
                />
                <span className="text-sm font-semibold text-slate-700">
                  Mission Juridique
                </span>
              </label>
              {newClient.missionJuridique === "oui" && (
                <div className="pl-6">
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Responsable Juridique
                  </label>
                  <select
                    name="juridique"
                    value={newClient.juridique}
                    onChange={handleSelectJuridique}
                    className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700"
                  >
                    <option value="">Juridique</option>
                    {juridiques.map((sup) => (
                      <option
                        key={sup.id}
                        value={`${sup.name} ${sup.lastName}`}
                      >
                        {sup.name} {sup.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Codes des missions cochées (Paramètres KYC/LAB) */}
            {["missionComptable", "missionSociale", "missionJuridique"].some((k) => newClient[k] === "oui") && (
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-500">Codes des missions (Paramètres KYC/LAB)</p>
                <MissionsRecurrentes
                  client={newClient}
                  missions={kycReferentiel?.listes?.missions || []}
                  onCodes={(champ, codes) => setNewClient((prev) => ({ ...prev, [champ]: codes }))}
                  peutCoder={ROLES_CODES_MISSION.includes(utilisateur?.role)}
                />
              </div>
            )}

            {/* Courrier de reprise pour ancien cabinet — en cours de développement */}
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newClient.reprisAncienCabinet === true}
                  onChange={handleRepriseAncienCabinetToggle}
                  className="accent-[#9B1B5D] w-4 h-4"
                />
                <span className="text-sm font-semibold text-slate-700">
                  Courrier de reprise pour ancien cabinet
                </span>
              </label>
              {newClient.reprisAncienCabinet === true && newClient.ancienCabinetNom && (
                <div className="pl-6">
                  <div className="flex items-center justify-between gap-2 h-10 px-3 rounded-lg bg-white border border-gray-200">
                    <span className="text-sm text-slate-700 truncate">
                      Ancien cabinet : <strong>{newClient.ancienCabinetNom}</strong>
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={handleOpenAncienCabinetOverlay}
                        className="text-xs font-medium text-[#9B1B5D] hover:underline"
                      >
                        Modifier
                      </button>
                      <button
                        type="button"
                        onClick={handleRemoveAncienCabinet}
                        className="text-slate-300 hover:text-slate-500 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mt-1.5">
                    📧 Une fois le client créé, un mail avec le courrier de reprise sera envoyé automatiquement à cet ancien cabinet.
                  </p>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* ── Commentaire Email ── */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <MessageSquare className="w-4 h-4 text-[#9B1B5D]" />
            <span className="text-sm font-semibold text-slate-700">
              Commentaire Email
            </span>
            <span className="text-xs text-slate-400 italic">(facultatif)</span>
          </div>
          <div className="p-4">
            <textarea
              name="commentaire"
              value={newClient.commentaire}
              onChange={handleChange}
              rows="3"
              placeholder="Ajouter un commentaire..."
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white outline-none focus:border-[#9B1B5D]/40 transition-all resize-none"
            />
          </div>
        </div>

        {error && <p className="text-xs text-center text-red-500">{error}</p>}
      </form>

      {/* Pied de page fixe */}
      <div className="shrink-0 flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-white">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 h-10 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isLoading}
          onClick={handleSubmit}
          className="flex items-center gap-2 px-8 h-10 bg-[#9B1B5D] hover:bg-[#7A1548] text-white text-sm font-medium rounded-lg shadow-sm shadow-[#9B1B5D]/20 transition-colors disabled:opacity-60"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Création en cours...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              Ajouter le Client
            </>
          )}
        </button>
      </div>

      {/* Panneau Groupement — rechercher ou créer */}
      {showGroupementPanel && (
        <div
          className="absolute inset-0 z-30 flex items-center justify-center rounded-2xl"
          style={{ background: "rgba(0,0,0,0.12)" }}
          onClick={() => setShowGroupementPanel(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full mx-8 overflow-hidden border border-slate-200"
            style={{ maxWidth: "480px" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3.5 bg-slate-50 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[#9B1B5D]" />
                <span className="text-sm font-bold text-[#9B1B5D]">
                  Groupement
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowGroupementPanel(false)}
                className="w-7 h-7 rounded-lg flex items-center justify-center border border-slate-200 bg-white hover:bg-slate-50 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Search input */}
            <div className="px-5 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl border border-slate-200 bg-slate-50/60 focus-within:border-[#9B1B5D]/30 focus-within:ring-1 focus-within:ring-[#9B1B5D]/10 transition-all">
                <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <input
                  autoFocus
                  type="text"
                  value={groupementQuery}
                  onChange={(e) =>
                    setGroupementQuery(e.target.value.toUpperCase())
                  }
                  placeholder="Rechercher un groupement…"
                  className="flex-1 text-xs text-slate-700 placeholder:text-slate-300 bg-transparent focus:outline-none"
                />
              </div>
            </div>

            {/* Résultats */}
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
              {(() => {
                const q = groupementQuery.trim();
                const filtered = groupements.filter((g) =>
                  g.toLowerCase().includes(q.toLowerCase())
                );
                const exactMatch = groupements.some(
                  (g) => g.toLowerCase() === q.toLowerCase()
                );

                return (
                  <>
                    {filtered.map((g) => (
                      <button
                        type="button"
                        key={g}
                        onClick={() => handleSelectGroupement(g)}
                        className="w-full flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-50/60 transition-colors text-left"
                      >
                        <span className="text-[13px] text-slate-700">{g}</span>
                        <Plus className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                      </button>
                    ))}

                    {filtered.length === 0 && q.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-8 gap-1.5">
                        <Search className="w-5 h-5 text-slate-200" />
                        <p className="text-xs text-slate-400">
                          Tapez pour rechercher un groupement existant
                        </p>
                      </div>
                    )}

                    {q.length > 0 && !exactMatch && (
                      <button
                        type="button"
                        onClick={() => handleSelectGroupement(q)}
                        className="w-full flex items-center gap-2 px-5 py-3 bg-[#9B1B5D]/5 hover:bg-[#9B1B5D]/10 transition-colors text-left"
                      >
                        <Plus className="w-3.5 h-3.5 text-[#9B1B5D] shrink-0" />
                        <span className="text-[13px] font-medium text-[#9B1B5D]">
                          Créer "{q}"
                        </span>
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Overlay Ancien cabinet — courrier de reprise (en cours de développement) */}
      {showAncienCabinetOverlay && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.35)" }}
          onClick={handleCancelAncienCabinetOverlay}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full mx-8 overflow-hidden border border-slate-200 flex flex-col"
            style={{ maxWidth: "920px", maxHeight: "85vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3.5 bg-slate-50 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[#9B1B5D]" />
                <span className="text-sm font-bold text-[#9B1B5D]">
                  Ancien cabinet — Courrier de reprise
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-bold uppercase tracking-wide">
                  🚧 En développement
                </span>
              </div>
              <button
                type="button"
                onClick={handleCancelAncienCabinetOverlay}
                className="w-7 h-7 rounded-lg flex items-center justify-center border border-slate-200 bg-white hover:bg-slate-50 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-0 overflow-y-auto">
              {/* Colonne gauche : formulaire */}
              <div className="p-5 space-y-3 md:border-r border-slate-100">
                <p className="text-xs text-slate-400">
                  Saisissez les coordonnées de l'ancien cabinet qui suivait ce
                  client — elles serviront au futur courrier de reprise.
                </p>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Nom du cabinet *
                  </label>
                  <input
                    autoFocus
                    type="text"
                    value={ancienCabinetForm.nom}
                    onChange={(e) =>
                      setAncienCabinetForm((prev) => ({ ...prev, nom: e.target.value }))
                    }
                    className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#9B1B5D] mb-1">
                    Email de l'ancien cabinet * — destinataire du courrier/PDF
                  </label>
                  <input
                    type="email"
                    required
                    value={ancienCabinetForm.email}
                    onChange={(e) =>
                      setAncienCabinetForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    placeholder="ex: contact@ancien-cabinet.fr"
                    className="h-10 w-full rounded-lg bg-white px-3 text-sm border-2 border-[#9B1B5D]/30 outline-none focus:border-[#9B1B5D] transition-all"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    C'est à cette adresse que le mail + PDF du courrier de reprise sera envoyé.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">
                      Adresse
                    </label>
                    <input
                      type="text"
                      value={ancienCabinetForm.adresse}
                      onChange={(e) =>
                        setAncienCabinetForm((prev) => ({ ...prev, adresse: e.target.value }))
                      }
                      className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">
                      Téléphone
                    </label>
                    <input
                      type="text"
                      value={ancienCabinetForm.telephone}
                      onChange={(e) =>
                        setAncienCabinetForm((prev) => ({ ...prev, telephone: e.target.value }))
                      }
                      className="h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all"
                    />
                  </div>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                  <p className="text-[11px] text-amber-700">
                    📧 Une fois le client créé, un mail avec le courrier de
                    reprise sera envoyé automatiquement à cet ancien cabinet.
                  </p>
                </div>
              </div>

              {/* Colonne droite : informations du courrier — éditables */}
              <div className="p-5 space-y-3 bg-slate-50/60">
                <p className="text-xs font-semibold text-slate-700">
                  Informations du courrier
                </p>
                <p className="text-[11px] text-slate-500">
                  Pré-remplies depuis le formulaire de création et le cabinet —
                  vous pouvez les corriger ici si besoin, sans modifier le
                  formulaire principal.
                </p>

                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Nom de notre cabinet
                  </label>
                  <input
                    type="text"
                    value={courrierForm.denominationCab}
                    onChange={(e) =>
                      setCourrierForm((prev) => ({ ...prev, denominationCab: e.target.value }))
                    }
                    className="h-9 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Adresse de notre cabinet
                  </label>
                  <input
                    type="text"
                    value={courrierForm.siegeCab}
                    onChange={(e) =>
                      setCourrierForm((prev) => ({ ...prev, siegeCab: e.target.value }))
                    }
                    className="h-9 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">
                      Ville (RCS)
                    </label>
                    <input
                      type="text"
                      value={courrierForm.villeRcsCab}
                      onChange={(e) =>
                        setCourrierForm((prev) => ({ ...prev, villeRcsCab: e.target.value }))
                      }
                      className="h-9 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">
                      Code postal
                    </label>
                    <input
                      type="text"
                      value={courrierForm.codePostalCab}
                      onChange={(e) =>
                        setCourrierForm((prev) => ({ ...prev, codePostalCab: e.target.value }))
                      }
                      className="h-9 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">
                      Lieu de signature du courrier
                    </label>
                    <input
                      type="text"
                      value={courrierForm.lieuDemande}
                      onChange={(e) =>
                        setCourrierForm((prev) => ({ ...prev, lieuDemande: e.target.value }))
                      }
                      className="h-9 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">
                      Date du courrier
                    </label>
                    <input
                      type="date"
                      value={courrierForm.dateDemande}
                      onChange={(e) =>
                        setCourrierForm((prev) => ({ ...prev, dateDemande: e.target.value }))
                      }
                      className="h-9 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all"
                    />
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-200">
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Expert-comptable signataire
                  </label>
                  <div className="h-9 w-full rounded-lg bg-slate-100 px-3 text-sm border border-gray-200 flex items-center text-slate-600">
                    {courrierForm.signataireNom || "Samuel — non trouvé dans la liste des experts-comptables"}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">
                    Pour l'instant, seul Samuel peut être signataire (pas d'autre choix disponible). Sa signature statique (déjà intégrée au modèle Word) sera utilisée.
                  </p>
                </div>

                <div className="pt-2 border-t border-slate-200 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">
                        Civilité du contact
                      </label>
                      <select
                        value={courrierForm.civiliteClient}
                        onChange={(e) =>
                          setCourrierForm((prev) => ({ ...prev, civiliteClient: e.target.value }))
                        }
                        className="h-9 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all"
                      >
                        <option value="">—</option>
                        <option value="M.">Monsieur</option>
                        <option value="Mme">Madame</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">
                        Forme sociale du client
                      </label>
                      <select
                        value={courrierForm.formeSociale}
                        onChange={(e) =>
                          setCourrierForm((prev) => ({ ...prev, formeSociale: e.target.value }))
                        }
                        className="h-9 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all"
                      >
                        <option value="">—</option>
                        {[
                          "E.I", "E.I.R.L", "EARL", "EURL", "SARL", "SASU", "SAS",
                          "SCI", "SCM", "SCCV", "LMNP", "GFA", "SC", "SCP", "SEL", "N/A",
                        ].map((item) => (
                          <option key={item} value={item}>{item}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">
                        Nom du contact
                      </label>
                      <input
                        type="text"
                        value={courrierForm.nomContact}
                        onChange={(e) =>
                          setCourrierForm((prev) => ({ ...prev, nomContact: e.target.value }))
                        }
                        className="h-9 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">
                        Prénom du contact
                      </label>
                      <input
                        type="text"
                        value={courrierForm.prenomContact}
                        onChange={(e) =>
                          setCourrierForm((prev) => ({ ...prev, prenomContact: e.target.value }))
                        }
                        className="h-9 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">
                      Nom du dossier / raison sociale
                    </label>
                    <input
                      type="text"
                      value={courrierForm.nomDossier}
                      onChange={(e) =>
                        setCourrierForm((prev) => ({ ...prev, nomDossier: e.target.value }))
                      }
                      className="h-9 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">
                      Date de début de mission
                    </label>
                    <input
                      type="date"
                      value={courrierForm.dateDebutMission}
                      onChange={(e) =>
                        setCourrierForm((prev) => ({ ...prev, dateDebutMission: e.target.value }))
                      }
                      className="h-9 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 px-5 py-3.5 bg-slate-50 border-t border-slate-100 shrink-0">
              <button
                type="button"
                disabled={isTestingEnvoiCourrier}
                onClick={handleTestEnvoiCourrier}
                title="Envoie un vrai mail de test avec le PDF généré à l'adresse email saisie ci-dessus"
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm font-medium hover:bg-amber-100 transition-colors disabled:opacity-60"
              >
                <Send className="w-3.5 h-3.5" />
                {isTestingEnvoiCourrier ? "Envoi du test..." : "🧪 Tester l'envoi du courrier"}
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCancelAncienCabinetOverlay}
                  className="h-9 px-4 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-white transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  disabled={isSavingAncienCabinet}
                  onClick={handleSaveAncienCabinet}
                  className="h-9 px-4 rounded-lg bg-[#9B1B5D] hover:bg-[#7A1548] text-white text-sm font-medium transition-colors disabled:opacity-60"
                >
                  {isSavingAncienCabinet ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Create;
